/* eslint-disable react-hooks/set-state-in-effect -- known false positive (react#34905) on async event handlers; not enforced by the app's build toolchain. */
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Copy, ExternalLink, Plus, Pencil } from "lucide-react";
import adminApi from "@/lib/adminApi";
import {
  PageHeader, AdminCard, DataTable, StatusPill, SearchInput, FilterChips,
  Drawer, ConfirmDialog, PageLoader, Btn, timeAgo,
} from "@/admin/components";
import { Avatar, Switch } from "@/components/jk/kit";
import { formatINR, formatDate } from "@/lib/format";
import { SubscriptionBlock } from "@/admin/SubscriptionBlock";

const FILTERS = ["All", "Free Tier", "Active", "Inactive", "Churned"];

export default function Astrologers() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("All");
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTab, setDetailTab] = useState("overview");
  const [clients, setClients] = useState(null);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await adminApi.get("/admin/astrologers", { params: { search, filter } });
    setRows(data.astrologers);
  }, [search, filter]);

  useEffect(() => { load(); }, [load]);

  const runAction = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      setConfirm(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const profileLink = (slug) => `${window.location.origin}/${slug}`;
  const copyLink = async (slug) => {
    const link = profileLink(slug);
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Profile link copied");
    } catch {
      toast.error("Couldn't copy the link");
    }
  };


  const columns = [
    {
      key: "full_name", label: "Astrologer",
      render: (r) => (
        <div className="flex items-start gap-3">
          <Avatar name={r.full_name} photo={r.photo_url} size={34} />
          <div>
            <div className="font-medium text-[#111]">{r.full_name || "—"}</div>
            <div className="text-[12px] text-[#999]">{r.email}</div>
            {r.city && (
              <span className="mt-1 inline-block rounded-full border border-[#e9e6e1] px-2 py-0.5 text-[11px] text-[#555]">{r.city}</span>
            )}
          </div>
        </div>
      ),
    },
    { key: "bookings_count", label: "Bookings", render: (r) => r.bookings_count },
    {
      key: "profile_active", label: "Active", sortable: false,
      render: (r) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Switch
            on={r.profile_active !== false}
            testid={`astro-active-${r.id}`}
            onChange={(val) => { setBusy(true); runAction(() => adminApi.post(`/admin/astrologers/${r.id}/set-active`, { active: val }), val ? "Astrologer activated" : "Astrologer deactivated — public page hidden"); }}
          />
        </div>
      ),
    },
    {
      key: "subscription_status", label: "Subscription",
      render: (r) => <StatusPill status={r.suspended ? "suspended" : r.subscription_status} />,
    },
    { key: "last_active", label: "Last Active", render: (r) => timeAgo(r.last_active) },
  ];

  return (
    <div data-testid="admin-astrologers-page">
      <PageHeader title="Astrologers" subtitle={rows ? `${rows.length} accounts` : ""}
        right={<Btn onClick={() => navigate("/admin/astrologers/new")} testid="new-astrologer-btn"><Plus size={16} strokeWidth={2.2} /> New astrologer</Btn>} />
      <div className="mb-5 flex flex-col gap-3">
        <SearchInput value={search} onChange={setSearch} placeholder="Search by name, email, or city..." testid="astro-search" />
        <FilterChips options={FILTERS} value={filter} onChange={setFilter} testidPrefix="astro-filter" />
      </div>

      <AdminCard className="overflow-hidden">
        {rows ? <DataTable columns={columns} rows={rows} onRowClick={async (a) => {
          setDetailLoading(true);
          setDetail(null);
          setDetailTab("overview");
          setClients(null);
          try {
            const { data } = await adminApi.get(`/admin/astrologers/${a.id}`);
            setDetail(data);
          } finally {
            setDetailLoading(false);
          }
        }} testid="astrologers-table" empty="No astrologers match your filters." /> : <PageLoader />}
      </AdminCard>

      {/* Detail drawer */}
      <Drawer open={!!detail || detailLoading} onClose={() => { setDetail(null); setDetailLoading(false); setDetailTab("overview"); setClients(null); }} title="Astrologer Profile" testid="astro-detail-drawer">
        {detailLoading ? (
          <PageLoader />
        ) : detail ? (
          <div className="space-y-6 text-[14px]">
            {/* Tab bar */}
            <div className="flex gap-1 rounded-[12px] bg-[#f5f4f0] p-1">
              {["overview", "clients"].map((tab) => (
                <button
                  key={tab}
                  data-testid={`drawer-tab-${tab}`}
                  onClick={async () => {
                    setDetailTab(tab);
                    if (tab === "clients" && clients === null) {
                      setClientsLoading(true);
                      try {
                        const { data } = await adminApi.get(`/admin/astrologers/${detail.profile.id}/clients`);
                        setClients(data.clients);
                      } catch {
                        setClients([]);
                      } finally {
                        setClientsLoading(false);
                      }
                    }
                  }}
                  className={`flex-1 rounded-[9px] py-1.5 text-[13px] font-semibold capitalize transition-colors ${detailTab === tab ? "bg-white text-[#111] shadow-sm" : "text-[#888] hover:text-[#444]"}`}
                >
                  {tab === "clients" ? `Clients (${detail.stats.unique_clients})` : "Overview"}
                </button>
              ))}
            </div>

            {detailTab === "clients" ? (
              <div>
                {clientsLoading ? (
                  <PageLoader />
                ) : clients && clients.length === 0 ? (
                  <p className="py-6 text-center text-[13px] text-[#aaa]">No clients yet.</p>
                ) : clients ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-[13px]">
                      <thead>
                        <tr className="border-b border-[#f0ede8]">
                          {["Name", "Email", "Mobile", "DOB", "Place of residence", "Date added"].map((h) => (
                            <th key={h} className="pb-2 pr-4 text-left text-[11px] font-semibold uppercase tracking-wide text-[#999]">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {clients.map((c) => (
                          <tr key={c.id} className="border-b border-[#f8f7f5]">
                            <td className="py-2.5 pr-4 font-medium text-[#111]">{c.full_name || "—"}</td>
                            <td className="py-2.5 pr-4 text-[#555]">{c.email || "—"}</td>
                            <td className="py-2.5 pr-4 text-[#555]">{c.mobile_number || "—"}</td>
                            <td className="py-2.5 pr-4 text-[#555]">{c.date_of_birth ? formatDate(c.date_of_birth) : "—"}</td>
                            <td className="py-2.5 pr-4 text-[#555]">{c.place_of_residence || "—"}</td>
                            <td className="py-2.5 text-[#999]">{c.created_at ? formatDate(String(c.created_at).slice(0, 10)) : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : null}
              </div>
            ) : (
            <>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-4">
                <Avatar name={detail.profile.full_name} photo={detail.profile.photo_url} size={56} />
                <div>
                  <div className="text-[17px] font-semibold text-[#111]">{detail.profile.full_name}</div>
                  <div className="text-[13px] text-[#999]">{detail.profile.email}</div>
                  <div className="text-[13px] text-[#999]">{detail.profile.city}</div>
                  {detail.profile.created_at && (
                    <div className="mt-0.5 text-[12px] text-[#bbb]">Signed up {formatDate(String(detail.profile.created_at).slice(0, 10))}</div>
                  )}
                </div>
              </div>
              {detail.profile.booking_slug && (
                <div className="flex shrink-0 items-center gap-1.5">
                  <button
                    data-testid="astro-copy-link"
                    onClick={() => copyLink(detail.profile.booking_slug)}
                    title="Copy profile link"
                    className="flex items-center gap-1.5 rounded-[10px] border border-[#e9e6e1] bg-white px-2.5 py-1.5 text-[12px] font-semibold text-[#111] transition-colors hover:bg-[#faf8f5]"
                  >
                    <Copy size={13} strokeWidth={2} /> Copy link
                  </button>
                  <a
                    data-testid="astro-open-link"
                    href={profileLink(detail.profile.booking_slug)}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Open profile in new tab"
                    className="flex h-[32px] w-[32px] items-center justify-center rounded-[10px] border border-[#e9e6e1] bg-white text-[#111] transition-colors hover:bg-[#faf8f5]"
                  >
                    <ExternalLink size={14} strokeWidth={2} />
                  </a>
                </div>
              )}
            </div>

            <Section title="Stats">
              <div className="grid grid-cols-3 gap-3">
                <Mini label="Bookings" value={detail.stats.total_bookings} />
                <Mini label="Revenue" value={formatINR(detail.stats.revenue)} />
                <Mini label="Clients" value={detail.stats.unique_clients} />
              </div>
            </Section>

            <Section title="Onboarding">
              <div className="space-y-1.5">
                {Object.entries({ Profile: "profile", Services: "services", Availability: "availability", Payout: "payout", Complete: "complete" }).map(([label, k]) => (
                  <div key={k} className="flex items-center justify-between">
                    <span className="text-[#555]">{label}</span>
                    <StatusPill tone={detail.onboarding[k] ? "green" : "gray"} label={detail.onboarding[k] ? "Done" : "Pending"} />
                  </div>
                ))}
              </div>
            </Section>

            <Section title="Services">
              {detail.services.length === 0 ? <p className="text-[#999]">No services added.</p> : detail.services.map((s) => (
                <div key={s.id} className="flex items-start justify-between py-1.5">
                  <span className="text-[#222]">{s.name}</span>
                  <div className="text-right text-[12px] text-[#999]">
                    {s.tier1_price_inr != null ? (
                      <div className="space-y-0.5">
                        <div>T1: {formatINR(s.tier1_price_inr)} · {s.tier1_duration_minutes ?? s.duration_minutes}m</div>
                        {s.tier2_price_inr != null && <div>T2: {formatINR(s.tier2_price_inr)} · {s.tier2_duration_minutes ?? s.duration_minutes}m</div>}
                        {s.tier3_price_inr != null && <div>T3: {formatINR(s.tier3_price_inr)} · {s.tier3_duration_minutes ?? s.duration_minutes}m</div>}
                      </div>
                    ) : (
                      <span>{s.duration_minutes}m · {formatINR(s.price_inr)}</span>
                    )}
                  </div>
                </div>
              ))}
            </Section>

            <Section title="Subscription">
              <SubscriptionBlock astroId={detail.profile.id} />
            </Section>

            <Section title="Payout (masked)">
              <Row label="UPI" value={detail.payout.upi || "—"} />
              <Row label="Account" value={detail.payout.account_number || "—"} />
              <Row label="IFSC" value={detail.payout.ifsc || "—"} />
            </Section>

            <Section title="Recent Bookings">
              {detail.recent_bookings.length === 0 ? <p className="text-[#999]">No bookings yet.</p> : detail.recent_bookings.map((b) => (
                <div key={b.id} className="flex items-center justify-between py-1">
                  <span className="text-[#222]">{b.service_name} · {formatDate(b.booking_date)}</span>
                  <StatusPill status={b.status} />
                </div>
              ))}
            </Section>
            </>
            )}

            <div className="space-y-2.5 border-t border-[#f2efea] pt-4">
              <button
                data-testid="astro-edit-btn"
                onClick={() => navigate(`/admin/astrologers/${detail.profile.id}/edit`)}
                className="flex h-[44px] w-full items-center justify-center gap-2 rounded-[12px] bg-[#111] text-[14px] font-semibold text-white transition-colors hover:bg-[#222]"
              >
                <Pencil size={16} strokeWidth={2} /> Edit profile
              </button>
              <button
                data-testid="astro-delete-btn"
                onClick={() => setConfirm({ astro: detail.profile })}
                className="flex h-[44px] w-full items-center justify-center gap-2 rounded-[12px] border border-[#e7b3ab] bg-white text-[14px] font-semibold text-[#c0392b] transition-colors hover:bg-[#fdf3f1]"
              >
                <Trash2 size={16} strokeWidth={2} /> Delete Account
              </button>
            </div>
          </div>
        ) : null}
      </Drawer>

      <ConfirmDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        busy={busy}
        title="Delete account?"
        message={`This permanently deletes ${confirm?.astro?.full_name} along with their services, availability, clients and bookings. This cannot be undone.`}
        confirmLabel="Delete permanently"
        onConfirm={async () => {
          setBusy(true);
          await runAction(() => adminApi.delete(`/admin/astrologers/${confirm.astro.id}`), "Account deleted");
          setDetail(null);
        }}
      />
    </div>
  );
}

const Section = ({ title, children }) => (
  <div>
    <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#999]">{title}</p>
    {children}
  </div>
);
const Row = ({ label, value }) => (
  <div className="flex items-center justify-between py-1">
    <span className="text-[#555]">{label}</span>
    <span className="text-[#222]">{value}</span>
  </div>
);
const Mini = ({ label, value }) => (
  <div className="rounded-[12px] bg-[#f9f8f5] p-3 text-center">
    <div className="text-[18px] font-bold text-[#111]">{value}</div>
    <div className="text-[11px] text-[#999]">{label}</div>
  </div>
);
